/**
 * features/payments/PaymentsManager.tsx
 * ------------------------------------------------------------------
 * Phase 2 — Payments. Lists recorded payments (each with its receipt
 * number) and provides a "Record payment" flow: pick an outstanding
 * invoice, then enter the payment.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { Receipt, Plus, Search, X, Download, Mail } from 'lucide-react';
import { usePayments } from './usePayments';
import { RecordPaymentModal } from './RecordPaymentModal';
import { useInvoices } from '@/features/invoices/useInvoices';
import { useSettings } from '@/contexts/SettingsContext';
import { downloadReceiptPdf, receiptPdfBase64, receiptFileName } from '@/lib/receiptPdf';
import { SendDocumentModal } from '@/features/common/SendDocumentModal';
import { formatPula } from '@/lib/money';
import { glassSurface } from '@/components/ui/glass';
import { DEFAULT_COMPANY_SETTINGS, type Invoice, type Payment, type PaymentMethod } from '@/types';
import { Button, Card, Input, Badge, Spinner } from '@/components/ui/primitives';

const METHOD_LABEL: Record<PaymentMethod, string> = {
  bank_transfer: 'Bank transfer',
  cash: 'Cash',
  cheque: 'Cheque',
  card: 'Card',
  mobile: 'Mobile money',
  other: 'Other',
};

function fmtDate(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function PaymentsManager() {
  const { payments, loading } = usePayments();
  const { invoices } = useInvoices();
  const { settings } = useSettings();
  const company = settings ?? DEFAULT_COMPANY_SETTINGS;
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState(false);
  const [pickSearch, setPickSearch] = useState('');
  const [payInvoice, setPayInvoice] = useState<Invoice | null>(null);
  const [sendReceipt, setSendReceipt] = useState<Payment | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payments.filter(
      (p) =>
        !q ||
        p.receiptNumber.toLowerCase().includes(q) ||
        p.invoiceNumber.toLowerCase().includes(q) ||
        p.clientName.toLowerCase().includes(q),
    );
  }, [payments, search]);

  const outstanding = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    return invoices
      .filter((i) => (i.status === 'issued' || i.status === 'part_paid') && i.balance > 0)
      .filter((i) => !q || (i.number ?? '').toLowerCase().includes(q) || i.client.name.toLowerCase().includes(q));
  }, [invoices, pickSearch]);

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by receipt, invoice, or client…"
            className="pl-9"
          />
        </div>
        <Button icon={<Plus size={16} />} onClick={() => setPicking(true)}>
          Record payment
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <Spinner label="Loading payments…" />
      ) : visible.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <Receipt size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search ? 'No payments match.' : 'No payments recorded yet.'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Receipt</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Invoice</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Method</th>
                <th className="px-4 py-3 text-right font-semibold">Amount</th>
                <th className="px-4 py-3 text-right font-semibold">Receipt PDF</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className="border-b border-black/5 last:border-0 dark:border-white/5">
                  <td className="px-4 py-3 font-semibold text-primary dark:text-gray-100">{p.receiptNumber}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(p.date)}</td>
                  <td className="px-4 py-3 text-primary dark:text-gray-200">{p.invoiceNumber}</td>
                  <td className="px-4 py-3 text-primary dark:text-gray-200">{p.clientName}</td>
                  <td className="px-4 py-3">
                    <Badge tone="neutral">{METHOD_LABEL[p.method]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-primary dark:text-gray-100">
                    {formatPula(p.amount)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => downloadReceiptPdf(p, company)}
                        title="Download receipt"
                        className="rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => setSendReceipt(p)}
                        title="Email receipt"
                        className="rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                      >
                        <Mail size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Outstanding-invoice picker */}
      {picking && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
          <div className="my-12 w-full max-w-lg">
            <div className={glassSurface('p-5')}>
              <div className="-mx-5 -mt-5 mb-3 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
                <div className="flex items-center gap-2 text-white">
                  <Receipt size={18} />
                  <h2 className="text-base font-semibold text-white">Outstanding invoices</h2>
                </div>
                <button
                  onClick={() => setPicking(false)}
                  className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={pickSearch}
                  onChange={(e) => setPickSearch(e.target.value)}
                  placeholder="Search invoices…"
                  className="pl-9"
                />
              </div>

              <div className="max-h-80 space-y-2 overflow-y-auto">
                {outstanding.length === 0 ? (
                  <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No outstanding invoices.
                  </p>
                ) : (
                  outstanding.map((inv) => (
                    <button
                      key={inv.id}
                      onClick={() => {
                        setPayInvoice(inv);
                        setPicking(false);
                      }}
                      className="flex w-full items-center justify-between rounded-xl border border-black/5 px-4 py-3 text-left transition hover:border-brand hover:bg-brandSoft/50 dark:border-white/10 dark:hover:bg-white/5"
                    >
                      <div>
                        <div className="font-semibold text-primary dark:text-gray-100">{inv.number}</div>
                        <div className="text-xs text-primaryLight dark:text-gray-400">{inv.client.name}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-primary dark:text-gray-100">{formatPula(inv.balance)}</div>
                        <div className="text-xs text-primaryLight dark:text-gray-400">due</div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {payInvoice && (
        <RecordPaymentModal invoice={payInvoice} onClose={() => setPayInvoice(null)} />
      )}

      {sendReceipt && (
        <SendDocumentModal
          heading={`Email receipt ${sendReceipt.receiptNumber}`}
          to={''}
          subject={`Receipt ${sendReceipt.receiptNumber} — ${company.tradingName || company.legalName}`}
          message={`Dear ${sendReceipt.clientName},\n\nThank you for your payment of ${formatPula(
            sendReceipt.amount,
          )} towards invoice ${sendReceipt.invoiceNumber}. Your receipt ${sendReceipt.receiptNumber} is attached.\n\nKind regards,\n${company.tradingName || company.legalName}`}
          filename={receiptFileName(sendReceipt)}
          contentBase64={receiptPdfBase64(sendReceipt, company)}
          relatedEntityType="payment"
          relatedEntityId={sendReceipt.id}
          onClose={() => setSendReceipt(null)}
        />
      )}
    </div>
  );
}
